import { api } from "../api/client";

/** Normalize a Frappe File.file_url to a path like /files/... or /private/files/... */
export function normalizeFrappeFileUrl(fileUrl) {
  if (!fileUrl || typeof fileUrl !== "string") return "";

  let path = fileUrl;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    path = new URL(path).pathname;
  }
  if (!path.startsWith("/")) path = `/${path}`;
  if (path.startsWith("/api/files/") || path.startsWith("/api/private/")) {
    path = path.replace(/^\/api/, "");
  }
  return path;
}

function extractFilename(fileUrl) {
  const path = normalizeFrappeFileUrl(fileUrl);
  const name = path.split("/").pop() || "download";
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

async function parseBlobError(blob) {
  try {
    const text = await new Response(blob).text();
    const json = JSON.parse(text);
    return json.message || json.exc_type || "File not found or permission denied";
  } catch {
    return "File not found or permission denied";
  }
}

async function requestFileBlob(url, params = {}) {
  const response = await api.get(url, {
    params,
    responseType: "blob",
    _skipSessionExpiry: true,
    _forceTokenAuth: true,
    validateStatus: () => true,
  });

  const contentType = response.headers?.["content-type"] || "";
  if (
    response.status >= 400 ||
    contentType.includes("application/json") ||
    contentType.includes("text/html") ||
    contentType.includes("text/plain")
  ) {
    const message = await parseBlobError(response.data);
    throw new Error(message);
  }

  return response;
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

async function fetchFrappeFileBlobAttempts({ file_url, fileId, privateFile }) {
  const fetchPath =
    process.env.NODE_ENV === "development" ? file_url : `/api${file_url}`;

  const attempts = [];

  if (privateFile && fileId && file_url) {
    attempts.push(() => requestFileBlob(fetchPath, { fid: fileId }));
  }

  if (file_url) {
    attempts.push(() =>
      requestFileBlob("/api/method/frappe.handler.download_file", { file_url })
    );
  }

  if (fileId) {
    attempts.push(() =>
      requestFileBlob("/api/method/frappe.core.doctype.file.file.download_file", {
        file_url,
        fid: fileId,
      })
    );
  }

  if (file_url) {
    attempts.push(() => requestFileBlob(fetchPath));
  }

  let lastError;
  for (const attempt of attempts) {
    try {
      const response = await attempt();
      return response.data;
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("Download failed");
}

export async function fetchFrappeFileBlob({
  fileUrl,
  fileId,
  isPrivate,
} = {}) {
  const file_url = normalizeFrappeFileUrl(fileUrl);
  if (!file_url && !fileId) throw new Error("Missing file URL");

  const privateFile =
    isPrivate === true || (isPrivate !== false && file_url.startsWith("/private/"));

  return fetchFrappeFileBlobAttempts({ file_url, fileId, privateFile });
}

/**
 * Download a Frappe attachment. Private files require the File document name (fid) for permission checks.
 *
 * @param {{ fileUrl: string, fileId?: string, downloadName?: string, isPrivate?: boolean }} options
 */
export async function downloadFrappeFile({
  fileUrl,
  fileId,
  downloadName,
  isPrivate,
} = {}) {
  const file_url = normalizeFrappeFileUrl(fileUrl);
  if (!file_url && !fileId) throw new Error("Missing file URL");

  const filename = downloadName || extractFilename(file_url || fileUrl);
  const privateFile =
    isPrivate === true || (isPrivate !== false && file_url.startsWith("/private/"));

  const blob = await fetchFrappeFileBlobAttempts({ file_url, fileId, privateFile });
  triggerBlobDownload(blob, filename);
}
